import { useState, useEffect, useContext, MouseEvent } from "react";
import IndeterminateCheckBoxOutlinedIcon from "@mui/icons-material/IndeterminateCheckBoxOutlined";
import AddBoxOutlinedIcon from "@mui/icons-material/AddBoxOutlined";
import CenterFocusStrongIcon from "@mui/icons-material/CenterFocusStrong";
import FolderIcon from "@mui/icons-material/Folder";
import { SimpleTreeView } from "@mui/x-tree-view/SimpleTreeView";
import { BpCheckbox, StyledTreeItem } from "../../helpers/treeHelper";
import { useRepositoryObjects } from "../../backend/hooks/pmxActivity/queryGetRepositoryObjects";
import { PmxActivityDetailsContext } from "../../contexts/pmxActivityDetailsProvider";
import {
  ExtendedRepositoryObject,
  GetRepositoryObjectsInput,
  Repo,
  RepositoryObjectFolder,
  RepositoryTree
} from "../../backend/types";
import Error from "../abstractComponents/error";

const getFolderUniqueName = (folder: ExtendedRepositoryObject) =>
  `${folder.repository.name}/${folder.absolutePath}`;

const toSelectedFolder = (
  folder: ExtendedRepositoryObject
): ExtendedRepositoryObject => {
  const absolutePath = folder.absolutePath || folder.name;
  return {
    ...folder,
    absolutePath,
    name: `${folder.repository.name}/${absolutePath}`
  };
};

export function InteractiveFileTransferImportFolders(props: {
  environmentId: string;
  setSuccessMessage: (message: { message: string; id: number } | null) => void;
  selectedFolders: ExtendedRepositoryObject[];
  setSelectedFolders: React.Dispatch<
    React.SetStateAction<ExtendedRepositoryObject[]>
  >;
  onImportSuccess?: () => void;
  hideReadOnlyFolders?: boolean;
}) {
  const { activityData } = useContext(PmxActivityDetailsContext);
  const [expandedItems, setExpandedItems] = useState<string[]>(["/"]);

  const {
    callMultipleRepository,
    callRepositoryObjects,
    treeHierarchy,
    getRepositoryObjectsError,
    foldersAndCount
  } = useRepositoryObjects(activityData?.mainRepository?.id, false);

  useEffect(() => {
    if (activityData) {
      const repoIds = [
        ...(props.hideReadOnlyFolders
          ? []
          : Array.isArray(activityData.readOnlyRepositories)
            ? activityData.readOnlyRepositories
                .filter((repo: Repo | null | undefined): repo is Repo => !!repo)
                .map((repo: Repo) => repo.id)
            : []),
        activityData.mainRepository.id
      ];
      const repositoryObjectsInput: GetRepositoryObjectsInput[] = repoIds.map(
        (repoId: string) => ({
          repoId: String(repoId),
          foldersOnly: true
        })
      );
      callMultipleRepository(repositoryObjectsInput, false);
    }
  }, [activityData, props.hideReadOnlyFolders]);
  useEffect(() => {
    if (!treeHierarchy?.length) return;
    const rootIds = treeHierarchy.map(
      (treeItem) => `${treeItem.repository.name}/`
    );
    setExpandedItems((prev) => {
      const missing = rootIds.filter((id) => !prev.includes(id));
      return missing.length === 0 ? prev : [...prev, ...missing];
    });
  }, [treeHierarchy]);

  const clickShowMore = async (showMore: RepositoryObjectFolder) => {
    await callRepositoryObjects({
      variables: {
        location: showMore.folderName,
        repoId: showMore.repository.id,
        pageNumber: showMore.currentPageNumber + 1,
        foldersOnly: true
      }
    });
  };

  // when cicking the checkbox
  const toggleFolderSelection = async (
    event: MouseEvent<HTMLButtonElement | HTMLDivElement>,
    clickedRepositoryObject: ExtendedRepositoryObject
  ) => {
    const clickedKey = getFolderUniqueName(clickedRepositoryObject);
    const isSelected = props.selectedFolders.some(
      (folder) => getFolderUniqueName(folder) === clickedKey
    );

    if (isSelected) {
      props.setSelectedFolders((prev) =>
        prev.filter((folder) => {
          if (folder.repository.id !== clickedRepositoryObject.repository.id) {
            return true;
          }
          return (
            !folder.absolutePath.startsWith(
              clickedRepositoryObject.absolutePath
            ) &&
            !clickedRepositoryObject.absolutePath.startsWith(
              folder.absolutePath
            )
          );
        })
      );
      return;
    }

    const { data } = await callRepositoryObjects({
      variables: {
        repoId: clickedRepositoryObject.repository.id,
        location: clickedRepositoryObject.absolutePath,
        foldersOnly: true
      }
    });

    const folders = data?.getRepositoryObjects?.repositoryObjects;

    const files = [
      toSelectedFolder(clickedRepositoryObject),
      ...(folders && folders.length > 0
        ? folders.map((folder: ExtendedRepositoryObject) =>
            toSelectedFolder({
              ...folder,
              absolutePath: folder.absolutePath || folder.name
            })
          )
        : [])
    ];

    setExpandedItems((prev) =>
      prev.includes(clickedKey) ? prev : [...prev, clickedKey]
    );

    props.setSelectedFolders((prev) => {
      return [...prev, ...files];
    });
  };

  // when clicking the expand/collapse icon or folder icon/row
  const handleToggle = async (event: any, nodeIds: string[]) => {
    event.persist();
    if (event.target.closest(".folder_select_checkbox")) {
      return;
    }

    const iconClicked = event.target.closest(".MuiTreeItem-root");
    const labelClicked = iconClicked
      ?.querySelector("svg")
      ?.getAttribute("data-testid");

    if (iconClicked && labelClicked === "AddBoxOutlinedIcon") {
      const absolutePath = iconClicked.getAttribute("data-absolutepath");
      const repoId = iconClicked.getAttribute("data-repoid");

      const { data } = await callRepositoryObjects({
        variables: {
          repoId,
          location: absolutePath,
          foldersOnly: true
        }
      });

      const folders = data?.getRepositoryObjects?.repositoryObjects;
      if (
        folders.length > 0 &&
        props.selectedFolders.some(
          (f) =>
            f.repository.id === repoId && f.absolutePath.includes(absolutePath)
        )
      ) {
        props.setSelectedFolders((prev) => {
          return [
            ...prev,
            ...folders.map((folder: ExtendedRepositoryObject) =>
              toSelectedFolder({
                ...folder,
                absolutePath: folder.absolutePath || folder.name
              })
            )
          ];
        });
      }
    }

    setExpandedItems(nodeIds);
  };

  const renderTreeNodes = (
    data: ExtendedRepositoryObject[],
    repoName: string = ""
  ) =>
    data.map((item) => {
      const folderChildren = (item.content || []).filter((child) =>
        child.absolutePath.endsWith("/")
      );

      const showMore: RepositoryObjectFolder | undefined = foldersAndCount.find(
        (folder) =>
          folder.folderName === item.absolutePath &&
          folder.count > folderChildren.length &&
          folder.repository.id === item.repository.id
      );

      const folderKey = getFolderUniqueName(item);
      const isChecked = props.selectedFolders.some(
        (x) => getFolderUniqueName(x) === folderKey
      );

      const label = (
        <>
          <BpCheckbox
            aria-label="Select Folder"
            checked={isChecked}
            sx={{ "& .MuiSvgIcon-root": { fontSize: 15 } }}
            className="folder_select_checkbox"
            onClick={(event) => toggleFolderSelection(event, item)}
          />
          <FolderIcon className="grey_color folder_icon" />
          {item.name}
        </>
      );

      if (folderChildren.length > 0) {
        return (
          <StyledTreeItem
            key={folderKey}
            itemId={folderKey}
            data-testid={"treeItem_" + item.absolutePath}
            data-folder-key={folderKey}
            data-absolutepath={item.absolutePath}
            data-repoid={item.repository.id}
            data-reponame={item.repository.name}
            label={label}
          >
            {renderTreeNodes(folderChildren, repoName)}
            {showMore && (
              <a
                className="btn ms-4 text-primary"
                onClick={() => clickShowMore(showMore)}
              >
                Show {showMore.count - folderChildren.length} more...
              </a>
            )}
          </StyledTreeItem>
        );
      }

      return (
        <StyledTreeItem
          itemId={folderKey}
          key={folderKey}
          data-folder-key={folderKey}
          data-absolutepath={item.absolutePath}
          data-repoid={item.repository.id}
          data-reponame={item.repository.name}
          label={label}
        >
          <StyledTreeItem
            key={"innerFolder" + folderKey}
            itemId={"innerFolder" + folderKey}
            label={<></>}
          />
        </StyledTreeItem>
      );
    });

  const readOnlyRepoIds = new Set(
    (activityData?.readOnlyRepositories || [])
      .filter((repo: Repo | null | undefined): repo is Repo => !!repo)
      .map((repo: Repo) => String(repo.id))
  );

  const renderTree = (tree: RepositoryTree[]) => {
    const visibleTree = props.hideReadOnlyFolders
      ? tree?.filter(
          (treeItem) => !readOnlyRepoIds.has(String(treeItem.repository.id))
        )
      : tree;

    return visibleTree?.map((treeItem) => (
      <SimpleTreeView
        key={`${treeItem.repository.name}-index`}
        slots={{
          expandIcon: AddBoxOutlinedIcon,
          collapseIcon: IndeterminateCheckBoxOutlinedIcon
        }}
        expandedItems={expandedItems}
        className="mt-2 text-break"
        disableSelection
        onExpandedItemsChange={handleToggle}
      >
        <StyledTreeItem
          key={`${treeItem.repository.name}/`}
          itemId={`${treeItem.repository.name}/`}
          data-absolutepath="/"
          data-repoid={treeItem.repository.id}
          data-reponame={treeItem.repository.name}
          label={
            <>
              <CenterFocusStrongIcon className="grey_color" />{" "}
              {treeItem.repository.name}
            </>
          }
        >
          {renderTreeNodes(
            treeItem.repositoryObjects,
            treeItem.repository.name
          )}
        </StyledTreeItem>
      </SimpleTreeView>
    ));
  };
  return (
    <>
      <p className="mb-2">
        Selecting folder(s) based on the <b>current</b> folder structure will
        import all file differences contained within that folder and its
        subdirectories.
      </p>
      <p className="red mb-2">
        <strong>Warning:</strong> &quot;Warning: This import may cause conflicts
        or overwrite existing files. To avoid this, cancel now and import files
        individually on the main page.&quot;
      </p>
      {renderTree(treeHierarchy)}
      {getRepositoryObjectsError && <Error error={getRepositoryObjectsError} />}
    </>
  );
}
